import { createContext } from 'react';
import {
  Drawer,
  Box,
  IconButton,
  Button,
  Divider,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { ChatHistory } from './ChatHistory';
import { UserMenu } from './UserMenu';

// Context for sidebar state
export const SidebarContext = createContext();

const SIDEBAR_WIDTH = 256;
const COLLAPSED_WIDTH = 64;

export const Sidebar = ({
  isOpen,
  isCollapsed,
  onClose,
  onCollapse,
  onExpand,
  onNewChat,
  onNewIncognitoChat,
  isIncognitoActive,
  onSearchChats,
  onSelectThread,
  threads,
  sharedThreads = [],
  currentThreadId,
  customGpts,
  user,
  onOpenProfile,
  onLogout,
  onOpenCompanyAdmin,
  apiUrl,
  onRenameThread,
  onDeleteThread,
  onLeaveThread,
  onPinThread
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const sidebarContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'background.paper',
        borderRight: 1,
        borderColor: 'divider',
      }}
    >
      {/* Desktop Header with Collapse Button */}
      {!isMobile && (
        <Box sx={{ display: 'flex', pl: '9px', pr: 1.5, pt: 1, pb: 1, justifyContent: 'flex-start' }}>
          <IconButton
            onClick={onCollapse}
            size="small"
            sx={{ color: 'text.secondary' }}
            title="Collapse sidebar"
          >
            <MenuOpenIcon />
          </IconButton>
        </Box>
      )}

      {/* Mobile Header with Close Button */}
      {isMobile && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Menu
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      {/* New Chat Button */}
      <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
        <Button
          fullWidth
          startIcon={<EditOutlinedIcon sx={{ fontSize: 18 }} />}
          onClick={onNewChat}
          sx={{
            justifyContent: 'flex-start',
            pl: 1.5,
            pr: 1.5,
            py: 0.75,
            textTransform: 'none',
            fontWeight: 600,
            color: '#2563eb',
            '&:hover': {
              bgcolor: 'rgba(37, 99, 235, 0.08)',
              color: '#1d4ed8',
            },
          }}
        >
          New Chat
        </Button>
      </Box>

      {/* Incognito Chat Button — toggle. While active (purple), clicking it
          EXITS incognito and starts a new regular chat. */}
      {onNewIncognitoChat && (
        <Box sx={{ px: 1.5, pt: 0.5, pb: 0.5 }}>
          <Button
            fullWidth
            startIcon={<VisibilityOffIcon sx={{ fontSize: 18 }} />}
            onClick={onNewIncognitoChat}
            title={isIncognitoActive
              ? 'Exit incognito — start a new regular chat'
              : 'Start an incognito chat (hidden from history, no memory)'}
            sx={{
              justifyContent: 'flex-start',
              pl: 1.5,
              pr: 1.5,
              py: 0.75,
              textTransform: 'none',
              fontWeight: 600,
              color: isIncognitoActive ? '#7c3aed' : 'text.secondary',
              bgcolor: isIncognitoActive ? 'rgba(124, 58, 237, 0.12)' : 'transparent',
              '&:hover': {
                bgcolor: 'rgba(124, 58, 237, 0.16)',
                color: '#7c3aed',
              },
            }}
          >
            {isIncognitoActive ? 'Exit Incognito' : 'Incognito Chat'}
          </Button>
        </Box>
      )}


      {/* Separator */}
      <Divider sx={{ mb: 1.5 }} />

      {/* Chat History */}
      <ChatHistory
        threads={threads}
        sharedThreads={sharedThreads}
        currentThreadId={currentThreadId}
        onSelectThread={onSelectThread}
        onCloseMobile={onClose}
        onRenameThread={onRenameThread}
        onDeleteThread={onDeleteThread}
        onLeaveThread={onLeaveThread}
        onSearchChats={onSearchChats}
        onPinThread={onPinThread}
        onNewChat={onNewChat}
        customGpts={customGpts}
      />

      {/* User Menu */}
      <UserMenu
        user={user}
        onOpenProfile={onOpenProfile}
        onLogout={onLogout}
        onOpenCompanyAdmin={onOpenCompanyAdmin}
      />
    </Box>
  );

  const collapsedContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: COLLAPSED_WIDTH,
        bgcolor: 'background.paper',
        borderRight: 1,
        borderColor: 'divider',
        pt: 1,
      }}
    >
      {/* Expand Button */}
      <IconButton
        onClick={onExpand}
        size="small"
        sx={{ mb: 1, color: 'text.secondary' }}
        title="Expand sidebar"
      >
        <ChevronRightIcon />
      </IconButton>

      {/* New Chat Icon */}
      <IconButton
        onClick={onNewChat}
        size="small"
        sx={{ mb: 1, color: '#2563eb', '&:hover': { bgcolor: 'rgba(37, 99, 235, 0.08)', color: '#1d4ed8' } }}
        title="New Chat"
      >
        <AddIcon />
      </IconButton>

      {/* Incognito Chat Icon — toggle (filled purple while active) */}
      {onNewIncognitoChat && (
        <IconButton
          onClick={onNewIncognitoChat}
          size="small"
          title={isIncognitoActive ? 'Exit incognito — start a new regular chat' : 'Start an incognito chat'}
          sx={{
            mb: 1,
            color: isIncognitoActive ? '#7c3aed' : 'text.secondary',
            bgcolor: isIncognitoActive ? 'rgba(124, 58, 237, 0.12)' : 'transparent',
            '&:hover': { bgcolor: 'rgba(124, 58, 237, 0.16)', color: '#7c3aed' },
          }}
        >
          <VisibilityOffIcon />
        </IconButton>
      )}

      {/* Search Icon */}
      <IconButton
        onClick={onSearchChats}
        size="small"
        sx={{ color: 'text.secondary' }}
        title="Search Chats"
      >
        <SearchIcon />
      </IconButton>
    </Box>
  );

  return (
    <>
      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={isOpen && isMobile}
        onClose={onClose}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        {sidebarContent}
      </Drawer>

      {/* Desktop Sidebar - Full */}
      {!isMobile && !isCollapsed && (
        <Box
          component="aside"
          id="sidebar"
          sx={{
            width: SIDEBAR_WIDTH,
            flexShrink: 0,
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          {sidebarContent}
        </Box>
      )}

      {/* Desktop Sidebar - Collapsed */}
      {!isMobile && isCollapsed && (
        <Box
          component="aside"
          id="sidebarCollapsed"
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            height: '100%',
          }}
        >
          {collapsedContent}
        </Box>
      )}
    </>
  );
};
